import PaymentMethodField from "../../components/Finance/PaymentMethodField"
import { useEffect, useState } from "react"
import { X, DollarSign } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { collectPayment, collectGeneralPayment } from "../../api/products/post"
import { useSelector } from "react-redux"
import { isCatalogStore } from "../../utils/storeCategory"
import toast from "react-hot-toast"
import { searchDebtors, searchGeneralDebtors } from "../../api/products/get"
import { notifyDashboardRefresh } from "../../utils/dashboardRefresh"

type Props = {
    isOpen: any
    storeId: any
    sale: any | null
    onClose: () => void
    onConfirm: (updatedSale: any) => void
}

export default function CollectPaymentModal({
    isOpen,
    storeId,
    sale,
    onClose,
    onConfirm,
}: Props) {
    const [amount, setAmount] = useState("")
    const [paymentMethod, setPaymentMethod] = useState("CASH")
    const [error, setError] = useState("")
    const [query, setQuery] = useState("")
    const [results, setResults] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [searchSale, setSearchSale] = useState<any>(null)

    const { userdata } = useSelector((state: any) => state.UserReducer)
    const isGeneralStore = isCatalogStore(userdata?.store)
    const selectedSale = sale || searchSale
    const amountPaid = selectedSale?.paid ?? selectedSale?.totalPaid ?? 0

    useEffect(() => {
        if (!isOpen) {
            setAmount("")
            setError("")
            setQuery("")
            setResults([])
            setLoading(false)
            setSubmitting(false)
            setSearchSale(null)
        }
    }, [isOpen])

    useEffect(() => {
        if (!query || query.length < 2) {
            setResults([])
            return
        }

        const fetchDebtors = async () => {
            try {
                setLoading(true)
                const data = isGeneralStore
                    ? await searchGeneralDebtors(storeId, query)
                    : await searchDebtors(storeId, query)
                setResults(Array.isArray(data) ? data : data?.data || [])
            } catch (e) {
                console.error(e)
                setResults([])
            } finally {
                setLoading(false)
            }
        }

        const debounce = setTimeout(fetchDebtors, 300)
        return () => clearTimeout(debounce)
    }, [isGeneralStore, query, storeId])

    const handleConfirm = async () => {
        const value = Number(amount)
        const saling = selectedSale

        if (!value || value <= 0) {
            setError("Montant invalide")
            return
        }

        if (!saling) {
            setError("Sélectionnez d'abord une vente à encaisser")
            return
        }

        if (value > saling.remaining) {
            setError("Le montant dépasse la dette restante")
            return
        }

        try {
            setSubmitting(true)
            setError("")
            const payment = isGeneralStore
                ? await collectGeneralPayment(storeId, saling._id, value, paymentMethod)
                : await collectPayment(saling._id, storeId, value, paymentMethod)
            await new Promise((resolve) => setTimeout(resolve, 2000))
            notifyDashboardRefresh()
            toast.success(`Paiement encaissé: ${value.toLocaleString()} FCFA`)

            onConfirm(payment)
            onClose()
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'encaissement")
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <AnimatePresence>
            {isOpen &&
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 px-3 py-4 backdrop-blur-sm sm:px-4 sm:py-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        className="mx-auto flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl sm:max-h-[calc(100vh-3rem)]"
                        initial={{ scale: 0.9, y: 40 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.9, y: 40 }}
                    >
                        {/* Header */}
                        <div className="flex justify-between items-center border-b border-slate-100 px-4 py-4 sm:px-6">
                            <h2 className="text-lg font-semibold">
                                Encaisser un paiement
                            </h2>
                            <button onClick={onClose}>
                                <X />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
                        {/* 🔍 SEARCH DEBTOR */}
                        <div className="relative space-y-2">
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={isGeneralStore
                                    ? "Rechercher client (nom, téléphone, produit)…"
                                    : "Rechercher client (nom ou téléphone)…"}
                                className="w-full border rounded-xl px-3 py-2"
                            />

                            {results?.length > 0 && (
                                <div className="absolute z-20 w-full bg-white border rounded-xl shadow-lg max-h-56 overflow-y-auto divide-y">
                                    {results?.map((d) => (
                                        <button
                                            key={d._id}
                                            onClick={() => {
                                                setError("")
                                                setQuery(`${d.clientName} • ${d.clientPhone}`)
                                                setSearchSale(d)
                                                setResults([])
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-blue-50 flex justify-between"
                                        >
                                            <div>
                                                <p className="text-sm font-medium">{d.clientName}</p>
                                                <p className="text-xs text-gray-500">
                                                    {d.modelId?.name || d.productName || d.label || "Produit"}
                                                </p>
                                            </div>
                                            <span className="text-red-600 font-semibold">
                                                {d.remaining.toLocaleString()} FCFA
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {loading && (
                                <p className="text-xs text-gray-400">Recherche…</p>
                            )}
                        </div>

                        {/* 🔎 SALE SUMMARY */}
                        {selectedSale && (
                            <div className="mt-6 bg-gray-50 rounded-xl border p-4 text-sm space-y-2">
                                <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Produit</span>
                                    <span className="text-right font-medium text-gray-800">
                                        {selectedSale.modelId?.name || selectedSale.productName || selectedSale.label || selectedSale.lastProduct || "-"}
                                    </span>
                                </div>

                                <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Client</span>
                                    <span className="text-right font-medium text-gray-800">
                                        {selectedSale.clientName}
                                    </span>
                                </div>

                                <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Déjà payé</span>
                                    <span className="text-right font-medium text-green-600">
                                        {amountPaid.toLocaleString()} FCFA
                                    </span>
                                </div>

                                <div className="flex justify-between gap-3 border-t pt-2">
                                    <span className="text-gray-700 font-medium">Reste à payer</span>
                                    <span className="text-right text-base font-bold text-red-600">
                                        {selectedSale.remaining.toLocaleString()} FCFA
                                    </span>
                                </div>
                            </div>
                        )}


                        {error && (
                            <div className="bg-red-50 text-red-600 text-sm p-2 rounded-lg">
                                {error}
                            </div>
                        )}

                        <PaymentMethodField value={paymentMethod} onChange={setPaymentMethod} disabled={submitting} />
                        {/* Montant */}
                        <div>
                            <label className="text-sm text-gray-600">
                                Montant encaissé (FCFA)
                            </label>
                            <div className="relative">
                                <DollarSign
                                    size={18}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder="Ex: 50000"
                                    className="pl-10 w-full border rounded-xl px-3 py-2 focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                        </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 border-t border-slate-100 px-4 py-4 sm:px-6">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 rounded-xl border"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={submitting}
                                className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {submitting ? "Encaissement..." : "Encaisser"}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            }
        </AnimatePresence>
    )
}
